const WRAPPED_URL_PAIRS: Record<string, string> = {
  '"': '"',
  "'": "'",
  '`': '`',
  '“': '”',
  '‘': '’',
  '<': '>',
  '(': ')',
  '[': ']',
  '{': '}',
  '（': '）',
  '【': '】',
  '《': '》',
};

function sanitizeA2uiUrlInput(rawUrl: string): string {
  let sanitized = rawUrl.trim();
  while (sanitized.length >= 2) {
    const closing = WRAPPED_URL_PAIRS[sanitized[0] || ''];
    if (!closing || !sanitized.endsWith(closing)) break;
    sanitized = sanitized.slice(1, -1).trim();
  }
  return sanitized;
}

function extractAuthority(value: string): string {
  const separatorIndex = value.search(/[/?#]/);
  return separatorIndex === -1 ? value : value.slice(0, separatorIndex);
}

function extractHost(authority: string): string | null {
  if (!authority || /\s/.test(authority)) return null;
  if (authority.startsWith('[')) {
    const closingIndex = authority.indexOf(']');
    if (closingIndex <= 1) return null;
    const portSuffix = authority.slice(closingIndex + 1);
    if (portSuffix && !/^:\d+$/.test(portSuffix)) return null;
    return authority.slice(1, closingIndex);
  }
  const lastColonIndex = authority.lastIndexOf(':');
  if (lastColonIndex > 0 && authority.indexOf(':') === lastColonIndex) {
    const portSuffix = authority.slice(lastColonIndex + 1);
    if (/^\d+$/.test(portSuffix)) return authority.slice(0, lastColonIndex);
  }
  if (authority.includes(':')) return null;
  return authority;
}

function isIpv4Host(host: string): boolean {
  const parts = host.split('.');
  if (parts.length !== 4) return false;
  return parts.every((part) => /^\d+$/.test(part) && Number(part) >= 0 && Number(part) <= 255);
}

function isIpv6Authority(authority: string): boolean {
  return /^\[[0-9A-Fa-f:.%]+\](?::\d+)?$/.test(authority);
}

function looksLikeLocalUrl(value: string): boolean {
  const authority = extractAuthority(value);
  if (isIpv6Authority(authority)) return true;
  const host = extractHost(authority);
  if (!host) return false;
  const normalizedHost = host.toLowerCase();
  return normalizedHost === 'localhost' || isIpv4Host(host);
}

function looksLikeWebHostUrl(value: string): boolean {
  const host = extractHost(extractAuthority(value));
  if (!host) return false;
  if (host.includes('..') || host.startsWith('.') || host.endsWith('.')) return false;
  return host.includes('.');
}

function buildWebUrlCandidate(sanitizedUrl: string): string | null {
  const lower = sanitizedUrl.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return sanitizedUrl;
  const malformedSchemeMatch = sanitizedUrl.match(/^(https?):*\/*(.+)$/i);
  if (malformedSchemeMatch) return `${malformedSchemeMatch[1]!.toLowerCase()}://${malformedSchemeMatch[2]!}`;
  if (sanitizedUrl.startsWith('//')) return `https:${sanitizedUrl}`;
  if (looksLikeLocalUrl(sanitizedUrl)) return `http://${sanitizedUrl}`;
  if (looksLikeWebHostUrl(sanitizedUrl)) return `https://${sanitizedUrl}`;
  return null;
}

function formatNormalizedUrl(url: URL): string {
  return (url.pathname === '/' || url.pathname === '') && !url.search && !url.hash ? `${url.protocol}//${url.host}` : url.toString();
}

export function normalizeA2uiWebUrl(rawUrl: string): string | null {
  const sanitized = sanitizeA2uiUrlInput(rawUrl);
  if (!sanitized) return null;
  const candidate = buildWebUrlCandidate(sanitized);
  if (!candidate) return null;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  const protocol = url.protocol.toLowerCase();
  if ((protocol !== 'http:' && protocol !== 'https:') || !url.host) return null;
  return formatNormalizedUrl(url);
}

export function readA2uiLocalHandlerWebUrlValue(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const rawUrl = typeof (value as Record<string, unknown>).url === 'string' ? ((value as Record<string, unknown>).url as string).trim() : '';
  if (!rawUrl) return null;
  return normalizeA2uiWebUrl(rawUrl);
}
